import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import propostasService from 'src/services/propostasService';
import v2_actionDetalhesLead from 'src/actions/v2/crm/v2_actionDetalhesLead';
import CompartilharPropostaDialog from '../CompartilharPropostaDialog';
import { PROPOSTAS_DESIGN } from '../Propostas.design';
import { ROTA_PROPOSTAS, caminhoEdicao } from '../propostasRotas';
import OrcamentoCliente from './OrcamentoCliente';
import OrcamentoItens, { formatCurrency } from './OrcamentoItens';
import OrcamentoMiniatura from './OrcamentoMiniatura';
import VisualizadorDocumento from '../editor/VisualizadorDocumento';
import {
  estadoInicial, montarPayloadOrcamento, propostaParaPreview, aplicarRespostaIaOrcamento,
  calcularTotais, addDias, itensPreenchidos, VALIDADE_PADRAO
} from './orcamentoModel';
import { ORCAMENTO_DESIGN as D } from './Orcamento.design';

const formatDataLonga = (d) => new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
const formatDataCurta = (d) => new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' }).format(d).replace(/\./g, '').replace(/ de /g, ' ');

// Tela de orçamento: formulário curto (para quem, itens, condições) com uma
// miniatura viva do documento ao lado. Grava uma Proposta com tipo 'orcamento'
// e blocos fixos — link público, PDF, assinatura e acompanhamento são os mesmos
// da proposta comercial. Não há editor de página aqui; a aparência (logo, cor,
// dados da empresa) vem do modelo.
export default function OrcamentoEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const isEditing = Boolean(id);

  // Mesmo contrato do editor completo: `voltar` é um nome de origem conhecido,
  // não uma URL livre, para a tela não virar um redirecionador aberto.
  const voltarParam = searchParams.get('voltar');
  const leadIdRetorno = searchParams.get('lead_id');
  const backTo = voltarParam === 'negocio' && leadIdRetorno
    ? `/dashboard/negocios?lead_id=${leadIdRetorno}`
    : ROTA_PROPOSTAS;

  const [proposta, setProposta] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [estado, setEstado] = useState(() => estadoInicial({
    leadIdParam: searchParams.get('lead_id'),
    chatIdParam: searchParams.get('chat_id')
  }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tamanhoReal, setTamanhoReal] = useState(false);
  const [popover, setPopover] = useState(null); // 'ia' | 'modelo' | null
  const [iaInstrucao, setIaInstrucao] = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [iaNota, setIaNota] = useState('');
  const abortRef = useRef(null);

  const readOnly = Boolean(proposta && proposta.status !== 'rascunho');

  useEffect(() => {
    if (!isEditing) { iniciarNovo(); return; }
    // Logo após criar, a URL troca para /editar com a proposta que já está no
    // estado: recarregar aqui só piscaria a tela e fecharia o diálogo de envio.
    if (proposta?._id === id) return;
    carregarProposta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setPopover(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const carregarProposta = async () => {
    try {
      setLoading(true);
      const response = await propostasService.buscar(id);
      if (response.error || !response.data) throw new Error(response.message || 'Orçamento não encontrado');
      const p = response.data;
      // Documento que não é orçamento abre no editor completo.
      if (p.tipo !== 'orcamento') {
        navigate(caminhoEdicao(p, searchParams.toString()), { replace: true });
        return;
      }
      setProposta(p);
      setEstado(estadoInicial({ proposta: p }));
    } catch (error) {
      toast({ title: 'Erro', description: error.message || 'Não foi possível carregar o orçamento', variant: 'destructive' });
      navigate(backTo);
    } finally {
      setLoading(false);
    }
  };

  const iniciarNovo = async () => {
    try {
      setLoading(true);
      const response = await propostasService.listarTemplates(user?.clienteId);
      const lista = response?.data || [];
      setTemplates(lista);
      const padrao = lista.find(t => t.padrao) || lista[0] || null;
      setTemplate(padrao);
      setEstado(prev => ({
        ...prev,
        template_id: padrao?._id || null,
        validade_dias: padrao?.validade_dias_default || prev.validade_dias
      }));

      const leadIdParam = searchParams.get('lead_id');
      if (leadIdParam) await carregarLead(leadIdParam);
    } catch (error) {
      console.error('Erro ao preparar orçamento:', error);
    } finally {
      setLoading(false);
    }
  };

  // Deep link vindo do negócio ou da conversa: o lead chega já vinculado.
  const carregarLead = async (leadId) => {
    try {
      const res = await v2_actionDetalhesLead(leadId);
      const lead = res.data?.data || res.data;
      if (lead?._id) aplicarLead(lead);
    } catch (error) {
      console.error('Erro ao carregar lead:', error);
    }
  };

  const aplicarLead = (lead) => {
    const contatoLead = lead.contato_id && typeof lead.contato_id === 'object' ? lead.contato_id : (lead.contato || {});
    setEstado(prev => ({
      ...prev,
      lead,
      lead_id: lead._id,
      chat_id: lead.whatsapp_chat_id?._id || lead.whatsapp_chat_id || prev.chat_id,
      contato: {
        nome: lead.nome || prev.contato.nome,
        email: prev.contato.email || contatoLead.info?.email || contatoLead.email || '',
        telefone: prev.contato.telefone || contatoLead.telefone || contatoLead.numero || ''
      }
    }));
  };

  const removerLead = () => setEstado(prev => {
    const chatDoLead = prev.lead?.whatsapp_chat_id
      && (prev.lead.whatsapp_chat_id?._id || prev.lead.whatsapp_chat_id) === prev.chat_id;
    return { ...prev, lead: null, lead_id: null, chat_id: chatDoLead ? null : prev.chat_id };
  });

  const patch = (p) => setEstado(prev => ({ ...prev, ...p }));
  const patchContato = (p) => setEstado(prev => ({ ...prev, contato: { ...prev.contato, ...p } }));
  const setTexto = (campo, texto) => setEstado(prev => ({ ...prev, [campo]: { ...prev[campo], texto, dirty: true } }));

  const trocarModelo = (t) => {
    setTemplate(t);
    setEstado(prev => ({ ...prev, template_id: t._id, validade_dias: prev.validade_dias || t.validade_dias_default || VALIDADE_PADRAO }));
    setPopover(null);
  };

  const preview = useMemo(() => propostaParaPreview(estado, { proposta, template }), [estado, proposta, template]);
  const { total } = calcularTotais(estado.itens, estado.desconto_global_percent);
  const validoAte = addDias(proposta?.created_at, estado.validade_dias);

  const handleSalvar = async () => {
    if (itensPreenchidos(estado.itens).length === 0) {
      toast({ title: 'Faltam os itens', description: 'Informe pelo menos um produto ou serviço com descrição ou valor.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = montarPayloadOrcamento(estado, { proposta });
      const response = isEditing
        ? await propostasService.editar(id, payload)
        : await propostasService.criar(user?.clienteId, payload);
      if (response.error) throw new Error(response.message);

      const salvo = response.data;
      setProposta(salvo);
      setEstado(estadoInicial({ proposta: salvo }));
      toast({
        title: isEditing ? 'Orçamento salvo' : 'Orçamento criado',
        description: isEditing ? 'As alterações foram gravadas.' : `Orçamento nº ${salvo.numero} pronto para enviar.`
      });
      if (!isEditing) {
        setShareOpen(true);
        navigate(caminhoEdicao(salvo, searchParams.toString()), { replace: true });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message || 'Não foi possível salvar o orçamento', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleIa = async () => {
    const instrucao = iaInstrucao.trim();
    if (!instrucao && !estado.chat_id && !estado.lead_id) {
      toast({ title: 'Descreva o orçamento', description: 'Diga o que inclui, quantidades e valores, ou vincule um lead que tenha conversa.' });
      return;
    }
    const mensagemUsuario = { role: 'user', texto: instrucao || '(montar a partir do contexto vinculado)', data: new Date().toISOString() };

    setIaLoading(true);
    setIaNota('');
    abortRef.current = new AbortController();
    try {
      const response = await propostasService.gerarComIa({
        chat_id: estado.chat_id || undefined,
        lead_id: estado.lead_id || undefined,
        instrucao,
        historico: (estado.mensagens_ia || []).map(m => ({ role: m.role, texto: m.texto })),
        // Sempre 'blocos': é o formato em que o agente devolve itens, forma de
        // pagamento e observações separados, que é o que este formulário aplica.
        estado: {
          titulo: preview.titulo,
          itens: estado.itens.map(i => ({ descricao: i.descricao, quantidade: i.quantidade, preco_unitario: i.preco_unitario })),
          modo_design: 'blocos',
          tem_logo: Boolean(preview.layout?.logo_url)
        }
      }, { signal: abortRef.current.signal });
      if (response.error) throw new Error(response.message || 'Não foi possível preencher com IA');

      const dados = response.data || {};
      const { mudou } = aplicarRespostaIaOrcamento(dados, estado);
      setEstado(prev => ({
        ...aplicarRespostaIaOrcamento(dados, prev).estado,
        mensagens_ia: [
          ...(prev.mensagens_ia || []),
          mensagemUsuario,
          { role: 'assistant', texto: dados.mensagem || 'Orçamento atualizado.', data: new Date().toISOString() }
        ]
      }));
      setIaNota(mudou.length
        ? `A IA preencheu ${mudou.join(', ')}. Revise os valores antes de ${isEditing ? 'salvar' : 'criar'}.`
        : (dados.mensagem || 'A IA não encontrou itens claros. Descreva produtos, quantidades e valores.'));
      setIaInstrucao('');
      setPopover(null);
    } catch (error) {
      const mensagem = propostasService.mensagemErroGerarIa(error, 'Não foi possível preencher com IA');
      setEstado(prev => ({
        ...prev,
        mensagens_ia: [...(prev.mensagens_ia || []), mensagemUsuario, { role: 'assistant', texto: mensagem, erro: true, data: new Date().toISOString() }]
      }));
      toast({ title: 'IA indisponível', description: mensagem, variant: 'destructive' });
    } finally {
      setIaLoading(false);
    }
  };

  if (loading) {
    return <div className={D.loading}>Carregando orçamento...</div>;
  }

  const statusCfg = proposta ? (PROPOSTAS_DESIGN.status[proposta.status] || PROPOSTAS_DESIGN.status.rascunho) : null;
  const subtitulo = proposta
    ? [statusCfg.label, proposta.contato?.nome].filter(Boolean).join(' · ')
    : 'Rascunho · ainda não salvo';
  const temContexto = Boolean(estado.chat_id || estado.lead_id);

  return (
    <div className={D.page}>
      <header className={D.topbar}>
        <div className={D.topbarLeft}>
          <button type="button" className={D.back} onClick={() => navigate(backTo)} title="Voltar" aria-label="Voltar">
            <Iconify icon="eva:arrow-back-outline" width={20} />
          </button>
          <div className="min-w-0">
            <h1 className={D.title}>{proposta ? `Orçamento nº ${proposta.numero}` : 'Novo orçamento'}</h1>
            <p className={D.subtitle}>{subtitulo}</p>
          </div>
        </div>
        <div className={D.topbarRight}>
          {proposta && (
            <button type="button" className={`${D.btn} ${D.btnGhost}`} onClick={() => setShareOpen(true)}>
              <Iconify icon="mdi:share-variant-outline" width={16} />
              Compartilhar
            </button>
          )}
          {!readOnly && (
            <button type="button" className={`${D.btn} ${D.btnPrimary}`} onClick={handleSalvar} disabled={saving}>
              {saving && <Iconify icon="mdi:loading" className="animate-spin" width={16} />}
              {isEditing ? 'Salvar alterações' : 'Criar orçamento'}
            </button>
          )}
        </div>
      </header>

      {readOnly && (
        <div className={D.banner}>
          <Iconify icon="mdi:lock-outline" width={14} />
          Este orçamento já foi enviado e não pode mais ser editado.
        </div>
      )}

      <div className={D.work}>
        <div className={D.workInner}>
          <section className={D.form}>
            <section className={D.block} aria-labelledby="orc-quem">
              <header className={D.blockHead}>
                <h2 id="orc-quem" className={D.blockTitle}>Para quem</h2>
                <p className={D.blockHelp}>Escolha um lead do CRM ou preencha o contato à mão.</p>
              </header>
              <OrcamentoCliente
                lead={estado.lead}
                contato={estado.contato}
                chatVinculado={Boolean(estado.chat_id)}
                onSelectLead={aplicarLead}
                onRemoverLead={removerLead}
                onChangeContato={patchContato}
                readOnly={readOnly}
              />
            </section>

            <section className={D.block} aria-labelledby="orc-itens">
              <header className={D.blockHead}>
                <h2 id="orc-itens" className={D.blockTitle}>Itens</h2>
                <p className={D.blockHelp}>Produtos e serviços com quantidade e preço. O total é calculado na hora.</p>
              </header>
              <OrcamentoItens
                itens={estado.itens}
                descontoGlobalPercent={estado.desconto_global_percent}
                onChangeItens={itens => patch({ itens })}
                onChangeDesconto={v => patch({ desconto_global_percent: v })}
                readOnly={readOnly}
              />
              {iaNota && (
                <div className={D.note} role="status">
                  <Iconify icon="mdi:creation" width={15} className="flex-shrink-0" />
                  <span className="flex-1">{iaNota}</span>
                  <button type="button" className={D.link} onClick={() => setIaNota('')}>Ok</button>
                </div>
              )}
            </section>

            <section className={D.block} aria-labelledby="orc-cond">
              <header className={D.blockHead}>
                <h2 id="orc-cond" className={D.blockTitle}>Condições</h2>
                <p className={D.blockHelp}>Validade, forma de pagamento e o que mais o cliente precisa saber.</p>
              </header>
              <div className={D.grid2}>
                <label className={D.field}>
                  <span className={D.label}>Validade</span>
                  <span className={D.suffix}>
                    <input
                      className={`${D.input} pr-14 tabular-nums`}
                      inputMode="numeric"
                      value={estado.validade_dias}
                      onChange={e => patch({ validade_dias: parseInt(e.target.value.replace(/\D/g, ''), 10) || 0 })}
                      onBlur={() => { if (!estado.validade_dias) patch({ validade_dias: VALIDADE_PADRAO }); }}
                      disabled={readOnly}
                    />
                    <span className={D.suffixText}>dias</span>
                  </span>
                  <span className={D.hint}>Vale até {formatDataLonga(validoAte)}.</span>
                </label>
                <label className={D.field}>
                  <span className={D.label}>Pagamento</span>
                  <input
                    className={D.input}
                    value={estado.pagamento.texto}
                    onChange={e => setTexto('pagamento', e.target.value)}
                    placeholder="Ex.: 50% na aprovação, 50% na entrega"
                    disabled={readOnly}
                  />
                </label>
              </div>
              <label className={`${D.field} mt-3.5`}>
                <span className={D.label}>Observações</span>
                <textarea
                  className={D.textarea}
                  rows={2}
                  value={estado.observacoes.texto}
                  onChange={e => setTexto('observacoes', e.target.value)}
                  placeholder="Garantia, prazo de execução, o que não está incluso…"
                  disabled={readOnly}
                />
              </label>
            </section>
          </section>

          <aside className={D.side} aria-label="Pré-visualização do orçamento">
            <p className={D.sideCap}>
              <span>Como o cliente vai ver</span>
              {!isEditing && templates.length > 1 ? (
                <button type="button" className={`${D.link} truncate`} onClick={() => setPopover(p => (p === 'modelo' ? null : 'modelo'))}>
                  Modelo: {template?.nome || '—'} · trocar
                </button>
              ) : template && <span className="truncate">Modelo: {template.nome}</span>}
            </p>

            <OrcamentoMiniatura proposta={preview} />

            <dl className={D.facts}>
              <div>
                <dt className={D.factLabel}>Total</dt>
                <dd className={D.factBig}>{formatCurrency(total)}</dd>
              </div>
              <div>
                <dt className={D.factLabel}>Válido até</dt>
                <dd className={D.factValue}>{formatDataCurta(validoAte)}</dd>
              </div>
            </dl>

            <div className={D.actions}>
              {popover === 'ia' && (
                <div className={D.pop}>
                  <p className={D.popTitle}>Preencher com IA</p>
                  <p className={D.popText}>
                    {temContexto
                      ? 'A IA usa a conversa e o lead vinculados como contexto. Descreva o que mais deve entrar.'
                      : 'Descreva o que inclui, quantidades e valores. Você revisa antes de criar.'}
                  </p>
                  <textarea
                    className={D.textarea}
                    rows={3}
                    value={iaInstrucao}
                    onChange={e => setIaInstrucao(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleIa(); } }}
                    placeholder="Ex.: 2 splits 12.000 BTU instalados, R$ 1.250 cada, com suporte de parede"
                    disabled={iaLoading}
                    autoFocus
                  />
                  <div className={D.popRow}>
                    <button type="button" className={`${D.btn} ${D.btnGhost} ${D.btnSmall}`} onClick={() => setPopover(null)}>Cancelar</button>
                    <button type="button" className={`${D.btn} ${D.btnPrimary} ${D.btnSmall}`} onClick={handleIa} disabled={iaLoading}>
                      {iaLoading ? <><Iconify icon="mdi:loading" className="animate-spin" width={14} />Gerando…</> : 'Preencher'}
                    </button>
                  </div>
                </div>
              )}

              {popover === 'modelo' && (
                <div className={D.pop}>
                  <p className={D.popTitle}>Modelo do documento</p>
                  <p className={D.popText}>Logo, dados da empresa e cor vêm do modelo. O conteúdo do orçamento não muda.</p>
                  <div className="space-y-0.5 max-h-56 overflow-y-auto">
                    {templates.map(t => (
                      <button
                        key={t._id}
                        type="button"
                        className={`${D.popOption} ${t._id === template?._id ? D.popOptionAtivo : ''}`}
                        onClick={() => trocarModelo(t)}
                      >
                        <span className="truncate">{t.nome}</span>
                        {t.padrao && <span className="text-[10px] font-semibold uppercase text-[#7C3AED] flex-shrink-0">Padrão</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!readOnly && (
                <button
                  type="button"
                  className={`${D.btn} ${D.btnSoft}`}
                  onClick={() => setPopover(p => (p === 'ia' ? null : 'ia'))}
                  disabled={iaLoading}
                >
                  <Iconify icon={iaLoading ? 'mdi:loading' : 'mdi:creation'} width={16} className={iaLoading ? 'animate-spin' : ''} />
                  {iaLoading ? 'Preenchendo com IA…' : 'Preencher com IA'}
                </button>
              )}
              <button type="button" className={`${D.btn} ${D.btnGhost}`} onClick={() => setTamanhoReal(true)}>
                <Iconify icon="mdi:magnify-plus-outline" width={16} />
                Ver em tamanho real
              </button>
            </div>
          </aside>
        </div>
      </div>

      {tamanhoReal && (
        <VisualizadorDocumento
          proposta={preview}
          titulo={proposta ? `Orçamento nº ${proposta.numero}` : 'Novo orçamento'}
          onClose={() => setTamanhoReal(false)}
        />
      )}

      {proposta && (
        <CompartilharPropostaDialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          proposta={proposta}
          temChat={Boolean(proposta.chat_id)}
        />
      )}
    </div>
  );
}
